import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  Typography,
  useTheme,
} from '@mui/material';
import {
  SwapHoriz as ExchangeIcon,
} from '@mui/icons-material';
import { EXCHANGE_SESSION_ROWS } from '../constants/profileConstants';
import { getExchangeSubscriptionsStyles, getExchangeRowStyles } from './ExchangeSubscriptionsCss';
import { getSessionTiming, isMarketOpen } from '../utils/marketTimingsLogic';

/**
 * Displays a stacked row list of exchange subscription statuses.
 * Each row shows the exchange name and an Enabled/Disabled badge.
 *
 * @param {Object} props
 * @param {string[]} props.subscribedExchanges - Array of subscribed exchange codes
 */
const ExchangeSubscriptions = React.memo(({ subscribedExchanges }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const styles = useMemo(() => getExchangeSubscriptionsStyles(theme, isDark), [theme, isDark]);
  const exchangeSessions = useSelector((state) => state.profile.exchangeSessions);

  // Derive a Set for O(1) lookups — only recomputed when the prop changes
  const subscribedSet = useMemo(
    () => new Set(subscribedExchanges || []),
    [subscribedExchanges]
  );

  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Card sx={styles.card}>
      {/* Header */}
      <Box sx={styles.header}>
        <Box sx={styles.headerLeft}>
          <ExchangeIcon sx={styles.headerIcon} />
          <Typography variant="subtitle2" sx={styles.headerTitle}>
            Exchange & Market Status
          </Typography>
        </Box>
      </Box>

      {/* Sub-header for column names */}
      <Box sx={styles.columnHeaderRow}>
        {/* Mobile Column Headers */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', justifyContent: 'space-between' }}>
          <Typography sx={styles.columnHeaderText}>
            Exchange
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ width: 65, display: 'flex', justifyContent: 'center' }}>
              <Typography sx={styles.columnHeaderText}>Subscription</Typography>
            </Box>
            <Box sx={{ width: 65, display: 'flex', justifyContent: 'center' }}>
              <Typography sx={styles.columnHeaderText}>Market</Typography>
            </Box>
          </Box>
        </Box>

        {/* Desktop Column Headers (Grid) */}
        <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: 'repeat(5, 1fr)', width: '100%', alignItems: 'center' }}>
          <Typography sx={{ ...styles.columnHeaderText, textAlign: 'left' }}>Exchange</Typography>
          <Typography sx={{ ...styles.columnHeaderText, textAlign: 'center' }}>Time</Typography>
          <Typography sx={{ ...styles.columnHeaderText, textAlign: 'center' }}>Days</Typography>
          <Typography sx={{ ...styles.columnHeaderText, textAlign: 'center' }}>Subscription</Typography>
          <Typography sx={{ ...styles.columnHeaderText, textAlign: 'center' }}>Market</Typography>
        </Box>
      </Box>

      {/* Exchange Rows */}
      <Box sx={styles.rowsContainer}>
        {EXCHANGE_SESSION_ROWS.map((row, index) => {
          const isEnabled = subscribedSet.has(row.code);
          const isLast = index === EXCHANGE_SESSION_ROWS.length - 1;

          return (
            <ExchangeRow
              key={row.sessionName}
              row={row}
              sessions={exchangeSessions}
              isEnabled={isEnabled}
              isLast={isLast}
              isDark={isDark}
              theme={theme}
              tick={tick}
            />
          );
        })}
      </Box>
    </Card>
  );
});

ExchangeSubscriptions.displayName = 'ExchangeSubscriptions';

/* ─── Individual exchange row ───────────────────────────────── */

const ExchangeRow = React.memo(({ row, sessions, isEnabled, isLast, isDark, theme, tick }) => {
  const styles = useMemo(
    () => getExchangeRowStyles(theme, isDark, isEnabled, isLast),
    [theme, isDark, isEnabled, isLast]
  );

  const timingInfo = getSessionTiming(row.sessionName, sessions);
  const isOpen = timingInfo ? isMarketOpen(row.type, timingInfo.open, timingInfo.close) : false;
  const timeDisplay = timingInfo ? timingInfo.timeDisplay : '-';
  const daysDisplay = row.type === 'daily_all_days' ? 'All Days' : 'Mon - Fri';

  const marketStatus = timingInfo ? (
    <Box sx={styles.marketBadge(isOpen)}>
      <Typography variant="caption" sx={styles.marketBadgeText(isOpen)}>
        {isOpen ? 'OPEN' : 'CLOSED'}
      </Typography>
    </Box>
  ) : (
    <Typography variant="caption" sx={styles.timingText}>-</Typography>
  );

  return (
    <Box sx={styles.row}>
      {/* ---------------- MOBILE LAYOUT ---------------- */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Left: Stack */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" sx={styles.exchangeName}>{row.label}</Typography>
            <Typography variant="caption" sx={styles.timingText}>{timeDisplay}</Typography>
            <Typography variant="caption" sx={styles.daysText}>({daysDisplay})</Typography>
          </Box>
        </Box>
        {/* Right: Badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 65, display: 'flex', justifyContent: 'center' }}>
            <Box sx={styles.badge}>
              <Typography variant="caption" sx={styles.badgeText}>{isEnabled ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          </Box>
          <Box sx={{ width: 65, display: 'flex', justifyContent: 'center' }}>
            {marketStatus}
          </Box>
        </Box>
      </Box>

      {/* ---------------- DESKTOP LAYOUT (GRID) ---------------- */}
      <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: 'repeat(5, 1fr)', width: '100%', alignItems: 'center' }}>
        {/* Col 1: Exchange */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start' }}>
          <Typography variant="caption" sx={styles.exchangeName}>{row.label}</Typography>
        </Box>
        
        {/* Col 2: Time */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="caption" sx={styles.timingText}>{timeDisplay}</Typography>
        </Box>
        
        {/* Col 3: Days */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="caption" sx={styles.daysText}>{daysDisplay}</Typography>
        </Box>
        
        {/* Col 4: Subscription Badge */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={styles.badge}>
            <Typography variant="caption" sx={styles.badgeText}>{isEnabled ? 'Enabled' : 'Disabled'}</Typography>
          </Box>
        </Box>
        
        {/* Col 5: Market Badge */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {marketStatus}
        </Box>
      </Box>
    </Box>
  );
});

ExchangeRow.displayName = 'ExchangeRow';

export default ExchangeSubscriptions;
